// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { handleTokenFailure, getAccessToken } from "./oauth";
const useHttp = () => {
  const MAX_TRIES = 3;

  const handleRequest = async (
    url,
    method,
    body,
    successFn,
    failFn,
    loadingFn,
    currentTry
  ) => {
    if (!currentTry) {
      currentTry = 0;
    }

    try {
      if (loadingFn) {
        loadingFn(true);
      }

      var encodedUrl = encodeURI(url);

      const response = await fetch(encodedUrl, {
        method: method,
        body: body ? JSON.stringify(body) : null,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + (await getAccessToken()),
        },
      });
      let responseBody = {
        message: "Error! Please try again by refreshing the page.",
      };
      try {
        responseBody = await response.json();
      } catch (e) {
        console.error(e);
      } finally {
        if (response.ok) {
          successFn(responseBody);
          if (loadingFn) {
            loadingFn(false);
          }
        } else {
          if (response.status === 401 && currentTry < MAX_TRIES) {
            responseBody = {
              message: "You are Unauthorized for this action/page!",
            };
            failFn(responseBody.message);
            handleTokenFailure(() =>
              handleRequest(
                encodedUrl,
                method,
                body,
                successFn,
                failFn,
                loadingFn,
                ++currentTry
              )
            );
          } else if (response.status === 403 && currentTry < MAX_TRIES) {
            responseBody = {
              message: "You are Forbidden for this action/page!",
            };
            failFn(responseBody.message);
            handleTokenFailure(() =>
              handleRequest(
                encodedUrl,
                method,
                body,
                successFn,
                failFn,
                loadingFn,
                ++currentTry
              )
            );
          } else if (response.status === 400) {
            failFn(responseBody.message);
          } else if (currentTry < MAX_TRIES) {
            handleRequest(
              encodedUrl,
              method,
              body,
              successFn,
              failFn,
              loadingFn,
              ++currentTry
            );
          } else {
            if (failFn) {
              failFn(responseBody.message);
            }
            if (loadingFn) {
              loadingFn(false);
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
      if (failFn) {
        failFn("Something went wrong!");
      }
    } finally {
      if (loadingFn) {
        loadingFn(false);
      }
    }
  };

  const handleFileRequest = async (
    url,
    method,
    body,
    successFn,
    failFn,
    loadingFn,
    currentTry
  ) => {
    if (!currentTry) {
      currentTry = 0;
    }

    try {
      if (loadingFn) {
        loadingFn(true);
      }

      var encodedUrl = encodeURI(url);

      const response = await fetch(encodedUrl, {
        method: method,
        body: body,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + (await getAccessToken()),
        },
      });
      const responseBody = await response.json();
      if (response.ok) {
        successFn(responseBody);
      } else {
        if (response.status === 401 && currentTry < MAX_TRIES) {
          responseBody = { message: "You are Unauthorized for this action!" };
          failFn(responseBody.message);
          handleTokenFailure(() =>
            handleFileRequest(
              encodedUrl,
              method,
              body,
              successFn,
              failFn,
              loadingFn,
              ++currentTry
            )
          );
        } else if (response.status === 403 && currentTry < MAX_TRIES) {
          responseBody = { message: "You are Forbidden for this action!" };
          failFn(responseBody.message);
          handleTokenFailure(() =>
            handleRequest(
              encodedUrl,
              method,
              body,
              successFn,
              failFn,
              loadingFn,
              ++currentTry
            )
          );
        } else if (currentTry < MAX_TRIES) {
          handleFileRequest(
            encodedUrl,
            method,
            body,
            successFn,
            failFn,
            loadingFn,
            ++currentTry
          );
        } else {
          console.error(responseBody.message);
          if (failFn) {
            failFn(responseBody.message);
          }
          if (loadingFn) {
            loadingFn(false);
          }
        }
      }
    } catch (err) {
      console.error(err);
      if (failFn) {
        failFn(err);
      }
    } finally {
      if (loadingFn) {
        loadingFn(false);
      }
    }
  };

  return {
    handleRequest,
    handleFileRequest,
  };
};

export default useHttp;
